import type { ComponentProps } from "react";
import { Checkbox as CheckboxPrimitive } from "radix-ui";
import { tv } from "tailwind-variants";
import type { VariantProps } from "tailwind-variants";

import { CheckIcon } from "../icons";
import { checkableStyles } from "./styles";

const checkboxVariants = tv({
  extend: checkableStyles,
  slots: {
    input: "rounded-sm",
    indicator: "stroke-secondary-600 stroke-1 text-secondary-600",
  },

  variants: {
    size: {
      base: { input: "translate-y-1" },
      lg: { input: "translate-y-0" },
    },
  },
});

const { input, indicator, itemRoot, label, description } = checkboxVariants();

const Input = ({
  size,
  className,
  isError: isErrorProp,
  "aria-invalid": ariaInvalid,
  ...props
}: ComponentProps<typeof CheckboxPrimitive.Root> &
  VariantProps<typeof checkboxVariants>) => {
  const isError = typeof ariaInvalid === "boolean" ? ariaInvalid : isErrorProp;

  return (
    <CheckboxPrimitive.Root
      className={input({ size, isError, className })}
      {...props}
    >
      <CheckboxPrimitive.Indicator className={indicator()}>
        <CheckIcon />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
};

const Item = ({
  className,
  children,
  size,
  isCard,
  "aria-invalid": ariaInvalid,
  ...props
}: ComponentProps<typeof CheckboxPrimitive.Root> &
  VariantProps<typeof checkboxVariants>) => {
  const isError = typeof ariaInvalid === "boolean" ? ariaInvalid : undefined;

  return (
    <label className={itemRoot({ className, isCard })}>
      <Input size={size} isError={isError} {...props} />

      <div className="min-w-0 grow break-words">{children}</div>
    </label>
  );
};

const Label = ({ className, ...props }: ComponentProps<"span">) => {
  return <span className={label({ className })} {...props} />;
};

const Description = ({ className, ...props }: ComponentProps<"span">) => {
  return <span className={description({ className })} {...props} />;
};

export type CheckedState = CheckboxPrimitive.CheckedState;

export const Checkbox = { Item, Label, Description, Input };
